/**
 * 분할 결제 방식 판정 — 8스텝 결제 흐름의 두뇌 (menu #817 M3 / #819 M5)
 *
 * ## 언제 분할을 묻지 않는가
 *
 * 설계는 "묻지 않는 경우" 를 두 가지로 못 박았다:
 *
 * - **매장이 분할을 끈 경우**(D5 kill-switch) — 합산만 한다.
 * - **진입자가 1명인 경우**(D4) — 대표 단독이면 Bill 1개, 현재 동작과 같다.
 *
 * 둘 다 "선택지를 0개로 만든다" 로 표현된다. 화면은 목록이 비면 방식 선택 단계를
 * 건너뛴다 — 조건을 화면마다 다시 쓰면 한 곳에서만 고치는 실수가 난다.
 *
 * ## 왜 최소 금액이 있는가
 *
 * D10 — 최소 MX$50, 잔액 초과 불가. 최소액은 PG 수수료가 소액 결제를 먹어치우기
 * 때문이고, 상한은 **과납이 곧 환불 작업**이기 때문이다. 상한은 서버가 준
 * 잔액이므로 하드코딩하지 않는다.
 */
import type { ClaimShareRequest } from '@/lib/api/split-payment';

/**
 * 결제 방식 4종 (프로토타입 flow.jsx).
 *
 * - `consumi` — 내가 먹은 것만 (항목 claim)
 * - `iguales` — 똑같이 나누기 (분모 = 인원)
 * - `articulos` — 항목을 골라서 (공유 항목은 항목별 분모, D7)
 * - `monto` — 금액을 직접 (부분 납부, D10)
 */
export type SplitMethod = 'consumi' | 'iguales' | 'articulos' | 'monto';

export const ALL_SPLIT_METHODS: readonly SplitMethod[] = [
  'consumi',
  'iguales',
  'articulos',
  'monto',
];

export interface SplitAvailability {
  /** 매장 정책 + 개발 게이트. `useSessionBills` 가 403 으로 판정한다. */
  isSplitAvailable: boolean;
  /** 이 세션에 합류한 사람 수(대표 포함). */
  comensalCount: number;
}

/**
 * 이 손님에게 보여줄 결제 방식.
 *
 * 빈 배열이면 **분할을 묻지 않는다** — 오류가 아니라 "나눌 이유가 없다" 는 상태다.
 */
export function availableSplitMethods({
  isSplitAvailable,
  comensalCount,
}: SplitAvailability): readonly SplitMethod[] {
  if (!isSplitAvailable) {
    return [];
  }

  // 1인이면 대표 단독이다. 자기 자신과 나눌 수는 없다.
  if (comensalCount < 2) {
    return [];
  }

  return comensalCount <= MAX_DENOMINATOR
    ? ALL_SPLIT_METHODS
    : ALL_SPLIT_METHODS.filter((method) => method !== 'iguales');
}

/** 방식 선택 단계를 띄울 것인가. */
export function shouldAskSplitMethod(availability: SplitAvailability): boolean {
  return availableSplitMethods(availability).length > 0;
}

/**
 * 부분 납부 금액 판정 결과.
 *
 * 실패 사유를 나눠 두는 이유는 화면이 서로 다른 말을 해야 하기 때문이다 —
 * "너무 적습니다" 와 "남은 것보다 많습니다" 는 손님이 할 행동이 다르다.
 */
export type PartialAmountVerdict = 'ok' | 'below_minimum' | 'exceeds_balance' | 'invalid';

export interface PartialAmountLimits {
  /** 서버가 준 최소 이체 금액(centavo). D10 기준 5000 = MX$50. */
  minimumMinor: number;
  /** 지금 남은 금액(centavo). 이보다 많이 내면 환불 작업이 된다. */
  outstandingMinor: number;
}

/**
 * 직접 입력한 금액이 낼 수 있는 금액인가.
 *
 * 소수 centavo 는 존재하지 않는다 — 정수가 아니면 입력 오류로 본다.
 */
export function validatePartialAmount(
  amountMinor: number,
  { minimumMinor, outstandingMinor }: PartialAmountLimits
): PartialAmountVerdict {
  if (!Number.isInteger(amountMinor) || amountMinor <= 0) {
    return 'invalid';
  }

  if (amountMinor > outstandingMinor) {
    return 'exceeds_balance';
  }

  // ★ 잔액이 최소액보다 적을 수 있다(마지막 한 조각). 그때 최소액을 강요하면 아무도
  //   그 세션을 닫을 수 없다 — 잔액 전액이면 통과시킨다.
  if (amountMinor < minimumMinor && amountMinor !== outstandingMinor) {
    return 'below_minimum';
  }

  return 'ok';
}

/**
 * 나눌 수 있는 최대 인원 — admin `ClaimBillShareRequest::MAX_DENOMINATOR` 와 같아야 한다.
 *
 * 서버가 22 명을 거절하는데 화면이 22 를 만들면, 손님은 이유를 알 수 없는 422 를 본다.
 */
export const MAX_DENOMINATOR = 20;

/** 서버는 `order_item_id: min:1` 을 요구한다. */
function isUsableItemId(itemId: number | undefined): itemId is number {
  return itemId !== undefined && Number.isInteger(itemId) && itemId >= 1;
}

function isUsableDenominator(denominator: number | undefined): denominator is number {
  return (
    denominator !== undefined &&
    Number.isInteger(denominator) &&
    denominator >= 2 &&
    denominator <= MAX_DENOMINATOR
  );
}

/**
 * 방식과 입력을 서버 요청으로 옮긴다.
 *
 * admin `ClaimBillShareRequest` 의 세 형태는 **배타**다 — 섞어 보내면 422 다.
 *
 * 서버가 거절할 것이 분명한 입력(분모 범위 밖, 정수 아님)은 요청을 만들지 않는다.
 * 보내 놓고 422 를 받아 번역하는 것보다, 애초에 만들지 않는 편이 화면이 정직하다.
 */
export function toClaimRequest(
  method: SplitMethod,
  input: { orderItemId?: number; denominator?: number; amountMinor?: number }
): ClaimShareRequest | null {
  switch (method) {
    case 'consumi':
    case 'articulos':
      if (!isUsableItemId(input.orderItemId)) {
        return null;
      }

      // 분모를 아예 주지 않았거나 1 이면 전량이다 — 1/1 을 굳이 분수로 보내지 않는다.
      if (input.denominator === undefined || input.denominator === 1) {
        return { order_item_id: input.orderItemId };
      }

      // ★ 여기서 전량으로 흘려보내면 안 된다. 분모 계산이 어긋나 0 이나 음수가 오는데
      //   "전량 청구" 로 바뀌면, 한 사람이 **항목 전체를 가져간다** — 조용히 돈이 틀린다.
      return isUsableDenominator(input.denominator)
        ? { order_item_id: input.orderItemId, numerator: 1, denominator: input.denominator }
        : null;

    case 'iguales':
      if (!isUsableDenominator(input.denominator)) {
        return null;
      }

      return {
        equal_denominator: input.denominator,
      };

    case 'monto':
      if (input.amountMinor === undefined || !Number.isInteger(input.amountMinor)) {
        return null;
      }

      return input.amountMinor > 0 ? { amount_minor: input.amountMinor } : null;
  }
}
